'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { formatters } from '@morpho-org/ui-v6/utils';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { CardNetApy } from '@/components/common/Card';
import { CardAnimationWrapper } from '@/components/common/Card/CardNetApy/CardAnimationWrapper';
import { CardNetApyHeader, type ModeCard } from '@/components/common/Card/CardNetApy/CardNetApyHeader';
import { CardNetApyRoot } from '@/components/common/Card/CardNetApy/CardNetApyParts';
import { CardNetApyInnerWrapper } from '@/components/common/Card/CardNetApy/CardNetApyParts';
import { CardReward } from '@/components/common/Card/CardReward';
import { EarnDashboardCardApySkeleton } from '@/components/EarnPage/common/EarnSkeletons/EarnDashboardCard';
import { DashboardClass, useDashboardContext } from '@/contexts/Dashboard/Dashboard.context';
import { calculateRewardSum } from '@/helpers/reward/calculateRewardSum';
import { useRewardsTable } from '@/hooks/reward/useRewardsTable';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import { calculateEarnAggregatedData } from '../hooks/useDashboardApyAggregatedData';
import { useDashboardEarnPositions } from '../hooks/useDashboardEarnPositions';
const percentageFormatter = formatters.percent.trailingZero(false).digits(2).unit('%');
interface DashboardHeroApyCardProps {
  chainIds: ChainId[];
}
const DashboardHeroApyCard = t0 => {
  const $ = _c(34);
  const {
    chainIds
  } = t0;
  const {
    address
  } = useViewedAccountAddress();
  const {
    isDashboardExpanded,
    isSmallDashboard
  } = useDashboardContext();
  const {
    earnPositions,
    loading,
    isError
  } = useDashboardEarnPositions(address);
  let t1;
  if ($[0] !== chainIds) {
    t1 = new Set(chainIds);
    $[0] = chainIds;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedChains = t1;
  let t2;
  if ($[2] !== selectedChains) {
    t2 = position => selectedChains.has(position.vault.chainId);
    $[2] = selectedChains;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const positions = earnPositions.filter(t2);
  const aggregatedData = calculateEarnAggregatedData(positions);
  const hasNoPositions = aggregatedData?.itemApys?.length === 0;
  const shouldPreventHoverClassname = isDashboardExpanded ? DashboardClass.PREVENT_DASHBOARD_CARD_HOVER_CLASS : undefined;
  const [mode, setMode] = useState("apy");
  const rewardData = useRewardsTable(address);
  const totalRewardClaimable = calculateRewardSum(rewardData.display.rewardsMerged);
  let t3;
  bb0: {
    if (mode === "apy") {
      t3 = formatters.percent.trailingZero(false).digits(2).default("0").of(aggregatedData?.apy, 18);
      break bb0;
    }
    if (rewardData.errorCode) {
      t3 = "-";
      break bb0;
    }
    t3 = formatters.short.digits(2).default("0").of(totalRewardClaimable, 18);
  }
  const formattedValue = t3;
  if (loading) {
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <EarnDashboardCardApySkeleton />;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    return t4;
  }
  const t4 = !!totalRewardClaimable;
  let t5;
  if ($[5] !== formattedValue || $[6] !== isDashboardExpanded || $[7] !== mode || $[8] !== rewardData.loading || $[9] !== setMode || $[10] !== t4) {
    t5 = <CardNetApyHeader variant="vault" value={formattedValue} isExpanded={isDashboardExpanded} onModeChange={setMode} mode={mode} hasRewardClaimable={t4} loading={rewardData.loading} />;
    $[5] = formattedValue;
    $[6] = isDashboardExpanded;
    $[7] = mode;
    $[8] = rewardData.loading;
    $[9] = setMode;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = !isSmallDashboard;
  let t7;
  if ($[12] !== aggregatedData || $[13] !== chainIds || $[14] !== hasNoPositions || $[15] !== isDashboardExpanded || $[16] !== isError || $[17] !== isSmallDashboard || $[18] !== mode || $[19] !== rewardData) {
    t7 = (isDashboardExpanded || isSmallDashboard) && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: 0.2
    }}>{mode === "apy" && <CardNetApy variant="vault" chainIds={chainIds} aggregatedApy={aggregatedData} apyTooltipContent={_temp} error={isError} hasNoPositions={hasNoPositions} />}{mode === "reward" && <CardReward rewardData={rewardData} source="dashboard_earn_card" />}</motion.div>;
    $[12] = aggregatedData;
    $[13] = chainIds;
    $[14] = hasNoPositions;
    $[15] = isDashboardExpanded;
    $[16] = isError;
    $[17] = isSmallDashboard;
    $[18] = mode;
    $[19] = rewardData;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t6 || $[22] !== t7) {
    t8 = <AnimatePresence initial={t6}>{t7}</AnimatePresence>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t5 || $[25] !== t8) {
    t9 = <CardNetApyInnerWrapper>{t5}{t8}</CardNetApyInnerWrapper>;
    $[24] = t5;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== shouldPreventHoverClassname || $[28] !== t9) {
    t10 = <CardNetApyRoot data-testid="dashboard-earn-hero-apy" className={shouldPreventHoverClassname}>{t9}</CardNetApyRoot>;
    $[27] = shouldPreventHoverClassname;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== isDashboardExpanded || $[31] !== isSmallDashboard || $[32] !== t10) {
    t11 = <CardAnimationWrapper isSmall={isSmallDashboard} isExpanded={isDashboardExpanded}>{t10}</CardAnimationWrapper>;
    $[30] = isDashboardExpanded;
    $[31] = isSmallDashboard;
    $[32] = t10;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  return t11;
};
export default DashboardHeroApyCard;
function _temp(t0) {
  const {
    allocation
  } = t0;
  return `${percentageFormatter.of(allocation, 18)} of your Earn deposits`;
}